/**
 * Consent ledger client (29 JUN 2026) — writes/reads `public.consent_log`.
 *
 * The app is invite-only (no self-serve signup), so terms/privacy/PDn acceptance
 * is captured at the FIRST authenticated entry (see components/ConsentGate.tsx),
 * and cookie choices are mirrored here once the visitor is authenticated.
 *
 * Each row is built to be *demonstrable* later: subject_email (survives account
 * deletion), doc_version + doc_hash (self-certifies the exact text accepted),
 * user_agent, and a server-set timestamp. All writes are best-effort: a failure
 * never blocks the UI. A localStorage map dedupes repeat writes per user+version.
 */

import { supabase } from '@/api/supabaseClient';
import { readConsent } from '@/lib/consent';
import { LEGAL_VERSION, LEGAL_DOC_HASHES, type LegalDocId } from '@/data/legal';

export type ConsentKind = 'terms' | 'privacy' | 'pdn_consent' | 'cookie_analytics';
export type ConsentAction = 'granted' | 'withdrawn';

export interface ConsentRowInput {
  kind: ConsentKind;
  action: ConsentAction;
  source: string;
  doc_version?: string;
}

/** Which legal document backs each consent kind (for the text fingerprint). */
const KIND_TO_DOC: Record<ConsentKind, LegalDocId> = {
  terms: 'terms',
  privacy: 'privacy',
  pdn_consent: 'consent',
  cookie_analytics: 'cookies',
};

const GUARD_KEY = 'qastudio.v1.consentLogged';

interface GuardEntry {
  termsVer?: string;
  cookie?: ConsentAction;
  cookieVer?: string;
}
type GuardMap = Record<string, GuardEntry>;

function readGuard(): GuardMap {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(GUARD_KEY) ?? '{}') as GuardMap;
  } catch {
    return {};
  }
}

function writeGuard(map: GuardMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(GUARD_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode — dedupe just won't persist */
  }
}

function userAgent(): string | null {
  return typeof navigator !== 'undefined' ? navigator.userAgent : null;
}

interface EnrichedRow {
  kind: ConsentKind;
  action: ConsentAction;
  doc_version: string;
  doc_hash: string;
  source: string;
}

/** Attach the document version + text fingerprint to each input row. */
function enrich(rows: ConsentRowInput[]): EnrichedRow[] {
  return rows.map((r) => ({
    kind: r.kind,
    action: r.action,
    doc_version: r.doc_version ?? LEGAL_VERSION,
    doc_hash: LEGAL_DOC_HASHES[KIND_TO_DOC[r.kind]],
    source: r.source,
  }));
}

/**
 * Preferred path: the `consent` edge function captures IP + user-agent
 * server-side and writes with the service role. Returns true on a 2xx.
 */
async function postToEdge(rows: EnrichedRow[]): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(
      /\/$/,
      '',
    );
    if (!token || !base) return false;
    const res = await fetch(`${base}/functions/v1/consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ rows }),
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Fallback: direct RLS insert (no IP) so consent is recorded even if the edge fn is down. */
async function directInsert(
  userId: string,
  email: string | null,
  rows: EnrichedRow[],
): Promise<boolean> {
  try {
    const ua = userAgent();
    const { error } = await supabase
      .from('consent_log')
      .insert(rows.map((r) => ({ user_id: userId, subject_email: email, ...r, user_agent: ua })));
    return !error;
  } catch {
    return false;
  }
}

/**
 * Insert rows into the ledger. Tries the edge function first (captures IP), then
 * falls back to a direct insert. Returns true on success. Never throws.
 */
async function insertRows(
  userId: string,
  email: string | null,
  rows: ConsentRowInput[],
): Promise<boolean> {
  if (rows.length === 0) return true;
  const enriched = enrich(rows);
  if (await postToEdge(enriched)) return true;
  return directInsert(userId, email, enriched);
}

/**
 * Has this user already accepted the Terms at the current document version?
 * Returns `null` if the lookup failed (network / pre-migration) so callers can
 * fail open instead of re-prompting on a transient error.
 */
export async function fetchTermsAccepted(
  userId: string,
  version: string = LEGAL_VERSION,
): Promise<boolean | null> {
  // Fast path: a prior acceptance on this device for this version.
  const guard = readGuard()[userId];
  if (guard?.termsVer === version) return true;
  try {
    const { data, error } = await supabase
      .from('consent_log')
      .select('id')
      .eq('user_id', userId)
      .eq('kind', 'terms')
      .eq('action', 'granted')
      .eq('doc_version', version)
      .limit(1);
    if (error) return null;
    return Array.isArray(data) && data.length > 0;
  } catch {
    return null;
  }
}

/**
 * Record first-login acceptance of the legal documents (Terms + Privacy + PDn
 * consent) and backfill the current cookie choice. Idempotent per device+version
 * via the localStorage guard.
 */
export async function recordFirstLoginConsents(
  userId: string,
  email: string | null,
  version: string = LEGAL_VERSION,
): Promise<boolean> {
  const rows: ConsentRowInput[] = [
    { kind: 'terms', action: 'granted', source: 'first_login', doc_version: version },
    { kind: 'privacy', action: 'granted', source: 'first_login', doc_version: version },
    { kind: 'pdn_consent', action: 'granted', source: 'first_login', doc_version: version },
  ];
  const cookie = readConsent();
  if (cookie) {
    rows.push({
      kind: 'cookie_analytics',
      action: cookie.analytics ? 'granted' : 'withdrawn',
      source: 'first_login',
      doc_version: version,
    });
  }
  const ok = await insertRows(userId, email, rows);
  if (ok) {
    const map = readGuard();
    map[userId] = {
      ...map[userId],
      termsVer: version,
      ...(cookie ? { cookie: cookie.analytics ? 'granted' : 'withdrawn', cookieVer: version } : {}),
    };
    writeGuard(map);
  }
  return ok;
}

/**
 * Mirror an explicit cookie-consent change to the ledger (called from the banner
 * when the visitor is authenticated). Deduped per user+version+choice.
 */
export async function logCookieChoice(
  userId: string | null,
  email: string | null,
  analytics: boolean,
  source = 'banner',
): Promise<void> {
  if (!userId) return;
  const action: ConsentAction = analytics ? 'granted' : 'withdrawn';
  const guard = readGuard();
  const entry = guard[userId];
  if (entry?.cookieVer === LEGAL_VERSION && entry.cookie === action) return; // already logged
  const ok = await insertRows(userId, email, [
    { kind: 'cookie_analytics', action, source, doc_version: LEGAL_VERSION },
  ]);
  if (ok) {
    guard[userId] = { ...entry, cookie: action, cookieVer: LEGAL_VERSION };
    writeGuard(guard);
  }
}
